import type { DailyCall } from '@daily-co/daily-js'
import { DailyProvider } from '@daily-co/daily-react'
import type { PendingStoreKiosk } from '@gozoki/api-types'
import RbacUnavailablePage from './communication/RbacUnavailablePage'
import { AuthContext, type AuthContextType } from '../utils/context/auth.context'
import {
  ClientEntryContext,
  type ClientEntryContextType,
} from '../utils/context/clientEntry.context'

import { AbilityProvider } from '@casl/react'
import { ThemeProvider } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createAppAbility } from '../utils/ability'

import {
  attachSocketToken,
  setAiSocketUrl,
  setGozokiApiWebSocketManager,
  setStreamSocketUrl,
  useAdminMe,
} from '@gozoki/api'
import * as Sentry from '@sentry/react'
import { useNavigate } from '@tanstack/react-router'
import { useRbacSession } from '../hooks/useRbacSession'
import { VisioContext, type VisioContextType } from '../utils/context/visio.context'
import { signOut, useReloadOnTokenChange } from '../utils/session-navigation'
import { materialTheme } from '../utils/theme/material.theme'

interface GlobalProviderProps {
  children: React.ReactNode
}

const GlobalProvider = ({ children }: GlobalProviderProps) => {
  const me = useAdminMe()
  const { query: rbacQuery, session: rbacSession } = useRbacSession(me.data?.id)
  const navigate = useNavigate()

  const [shouldStartCall, setShouldStartCall] = useState(false)
  const [kioskId, setKioskId] = useState<number | null>(null)
  const [storeId, setStoreId] = useState<number | null>(null)
  const [includeVideo, setIncludeVideo] = useState(false)
  const [openingEventId, setOpeningEventId] = useState<number | null>(null)
  const [callLabel, setCallLabel] = useState<string | null>(null)
  const [isCallInProgress, setIsCallInProgress] = useState(false)
  const [callObject, setCallObject] = useState<null | DailyCall>(null)
  const callObjectRef = useRef<null | DailyCall>(null)
  const disposedCallObjects = useRef(new WeakSet<DailyCall>())
  const callReservedRef = useRef(false)

  const [pendingKiosks, setPendingKiosks] = useState<PendingStoreKiosk[]>([])

  const askCall = useCallback(
    (
      newKioskId: number,
      newStoreId: number,
      newIncludeVideo: boolean,
      newOpeningEventId?: number,
      newCallLabel?: string,
    ) => {
      if (callReservedRef.current || isCallInProgress || shouldStartCall) {
        return
      }

      callReservedRef.current = true
      setKioskId(newKioskId)
      setStoreId(newStoreId)
      setIncludeVideo(newIncludeVideo)
      setOpeningEventId(newOpeningEventId ?? null)
      setCallLabel(newCallLabel ?? null)
      setShouldStartCall(true)
    },
    [isCallInProgress, shouldStartCall],
  )

  const startCall = useCallback(() => {
    setShouldStartCall(false)
    setIsCallInProgress(true)
  }, [])

  const updateCallObject = useCallback((nextCallObject: DailyCall | null) => {
    callObjectRef.current = nextCallObject
    setCallObject(nextCallObject)
  }, [])

  const clearCallObject = useCallback(() => {
    const activeCallObject = callObjectRef.current
    callObjectRef.current = null
    setCallObject(null)
    if (!activeCallObject || disposedCallObjects.current.has(activeCallObject)) return

    disposedCallObjects.current.add(activeCallObject)
    void (async () => {
      try {
        await activeCallObject.leave()
      } catch {
        // Daily may already have left the room; destruction still has to run.
      }
      try {
        await activeCallObject.destroy()
      } catch {
        // Destruction is idempotent from the application's point of view.
      }
    })()
  }, [])

  const endCall = useCallback(() => {
    clearCallObject()
    setShouldStartCall(false)
    setKioskId(null)
    setStoreId(null)
    setOpeningEventId(null)
    setCallLabel(null)
    setIsCallInProgress(false)
    callReservedRef.current = false
  }, [clearCallObject])

  const askEntry = useCallback((newPendingKiosk: PendingStoreKiosk) => {
    setPendingKiosks((prevKiosks) => [...prevKiosks, newPendingKiosk])
  }, [])

  const openClientForPendingKiosk = useCallback(
    (processedKioskId: number, openPage: boolean) => {
      setPendingKiosks((prevKiosks) => {
        const kioskToProcess = prevKiosks.find((kiosk) => kiosk.kioskId === processedKioskId)

        if (kioskToProcess && openPage) {
          navigate({
            search: { storeId: kioskToProcess.storeId },
            to: '/cameras/new',
          })
        }
        return prevKiosks.filter((kiosk) => kiosk.kioskId !== processedKioskId)
      })
    },
    [navigate],
  )

  useEffect(() => {
    if (me?.data?.socketToken) {
      attachSocketToken(me.data.socketToken)
    }
    if (me?.data?.socketAiUrl) {
      setAiSocketUrl(me.data.socketAiUrl)
    }
    if (me?.data?.socketStreamUrl) {
      setStreamSocketUrl(me.data.socketStreamUrl)
    }
    if (me?.data?.socketUrl) {
      setGozokiApiWebSocketManager(me.data.socketUrl)
    }
  }, [me])

  // Email de l'admin dans le contexte Sentry, pour la page chargée avec sa session.
  useEffect(() => {
    Sentry.setUser(me.data ? { email: me.data.email } : null)
  }, [me.data])

  // Tout changement d'identité recharge la page (`utils/session-navigation.ts`) ; ici, un jeton
  // changé ou effacé dans un autre onglet.
  useReloadOnTokenChange()

  const rbacGrantsV2 = useMemo(() => rbacSession.contract?.grantsV2 ?? [], [rbacSession.contract])
  const currentAbility = useMemo(() => createAppAbility(rbacGrantsV2), [rbacGrantsV2])

  const authContextValue: AuthContextType = useMemo(
    () => ({
      // Un compte connecté est une session BO valide, zéro grant compris : la racine affiche
      // alors l'état dédié avec déconnexion. Sans contrat exploitable, l'écran bloquant ci-dessous
      // remplace toute l'interface.
      authenticated: !!me.data,
      user: me.data ?? null,
      isLoading: me.isLoading || (!!me.data && rbacQuery.isLoading),
      logout: signOut,
      rbacLoading: !!me.data && rbacQuery.isLoading,
      rbacGrantsV2,
    }),
    [me.data, me.isLoading, rbacQuery.isLoading, rbacGrantsV2],
  )

  const visioContextValue: VisioContextType = useMemo(
    () => ({
      shouldStartCall,
      kioskId,
      storeId,
      includeVideo,
      openingEventId,
      isCallInProgress,
      callObject,
      callLabel,
      setCallObject: updateCallObject,
      clearCallObject,
      askCall,
      startCall,
      endCall,
    }),
    [
      kioskId,
      shouldStartCall,
      storeId,
      includeVideo,
      openingEventId,
      isCallInProgress,
      callObject,
      callLabel,
      updateCallObject,
      clearCallObject,
      endCall,
      startCall,
      askCall,
    ],
  )

  const clientEntryContextValue: ClientEntryContextType = useMemo(
    () => ({
      pendingKiosks,
      askEntry,
      openClientForPendingKiosk,
    }),
    [pendingKiosks, askEntry, openClientForPendingKiosk],
  )

  if (me.isLoading || rbacSession.state === 'loading') {
    return null
  }

  // Aucun droit lisible : on bloque plutôt que d'ouvrir l'interface en grand.
  if (rbacSession.state === 'unavailable') {
    return <RbacUnavailablePage query={rbacQuery} onLogout={() => void signOut()} />
  }

  return (
    <AbilityProvider value={currentAbility}>
      <AuthContext.Provider value={authContextValue}>
        <VisioContext.Provider value={visioContextValue}>
          <DailyProvider callObject={callObject}>
            <ClientEntryContext.Provider value={clientEntryContextValue}>
              <ThemeProvider theme={materialTheme}>
                {/* Bandeau discret : le contrat de la session reste en place pendant que la
                    requête retente d'elle-même. */}
                {rbacSession.degraded && (
                  <div
                    role="status"
                    className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center pb-3"
                  >
                    <span className="rounded-full border border-sidebar-border bg-background/95 px-3 py-1 text-muted-foreground text-xs shadow-xs">
                      Autorisations non actualisées, nouvel essai…
                    </span>
                  </div>
                )}
                {children}
              </ThemeProvider>
            </ClientEntryContext.Provider>
          </DailyProvider>
        </VisioContext.Provider>
      </AuthContext.Provider>
    </AbilityProvider>
  )
}

export default GlobalProvider
